import React, { useEffect, useState } from "react";
import {
  Box, Paper, CardContent, TextField, Grid, Button, Stepper, Step, StepLabel,
  LinearProgress, Typography, RadioGroup, FormControlLabel, Radio,
  FormControl, InputLabel, Select, MenuItem, Checkbox, Alert, Divider
} from "@mui/material";
import {
  PersonOutline, SpaOutlined, CleaningServicesOutlined, TaskAltOutlined,
  CloudUploadOutlined, ArrowBackRounded, ArrowForwardRounded, SendRounded,
  SolarPowerRounded
} from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import { motion, AnimatePresence } from "framer-motion";

const stepData = [
  { label: "Контакт", icon: <PersonOutline /> },
  { label: "Замърсявания", icon: <SpaOutlined /> },
  { label: "Почистване", icon: <CleaningServicesOutlined /> },
  { label: "Потвърждение", icon: <TaskAltOutlined /> }
];

const pollutantOptions = {
  dust: "Прах",
  bees: "Замърсяване от пчели",
  birds: "Замърсяване от птици",
  limescale: "Котлен камък",
  lichens: "Лишеи / мъх",
  rust: "Ръжда / метални оксиди",
  cement: "Циментов прах",
  paint: "Замърсяване от боя"
};

const createInitialForm = () => ({
  firstName: "", lastName: "", contactEmail: "", contactPhone: "",
  pollutants: {
    dust: false, bees: false, birds: false, limescale: false,
    lichens: false, rust: false, cement: false, paint: false,
    other: false, otherText: ""
  },
  region: "", cleanedBefore: "", cleaningMethod: "", cleaningDescription: "",
  parkPower: "", panelSpecs: "", panelCountOrArea: "", source: ""
});

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" }
};

function formatRange(value) {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length !== 2) return value;
  return parts.map((part) => {
    const n = Number(part);
    return Number.isFinite(n)
      ? new Intl.NumberFormat("bg-BG", { maximumFractionDigits: 2 }).format(n)
      : part;
  }).join("–");
}

function getCalculatorPrefill() {
  const params = new URLSearchParams(window.location.search);
  const power = params.get("power");
  const panels = params.get("panels");
  const area = params.get("area");
  const swp = params.get("swp");
  const swpCost = params.get("swpCost");

  if (!power && !panels && !area && !swp && !swpCost) return null;

  return {
    parkPower: power ? `${power} kWp` : "",
    panelSpecs: "Площ на панел: 2,2 m²; разход на готов разтвор: 105–206 ml/m²; разреждане: 1:50",
    panelCountOrArea: [
      panels ? `Брой панели: ${formatRange(panels)}` : "",
      area ? `Обща площ: ${formatRange(area)} m²` : "",
      swp ? `Концентрат SWP: ${formatRange(swp)} л` : "",
      swpCost ? `Ориентировъчна цена на концентрата: ${formatRange(swpCost)} €` : ""
    ].filter(Boolean).join("; ")
  };
}

export default function FancyWizard({
  submitUrl = "/api/submit.php",
  recaptchaKey = "6LdiUyMsAAAAAB-jAieKKClmVR_z-WndzJVWImT7"
}) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(createInitialForm);
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    const prefill = getCalculatorPrefill();
    if (prefill) setForm((previous) => ({ ...previous, ...prefill }));
  }, []);

  const togglePollutant = (key) => {
    setForm((previous) => ({
      ...previous,
      pollutants: { ...previous.pollutants, [key]: !previous.pollutants[key] }
    }));
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.some((file) => !file.type.startsWith("image/"))) {
      setImageError("Моля, изберете само изображения (JPG/PNG).");
      event.target.value = "";
      return;
    }
    if (files.length > 3) {
      setImageError("Можете да прикачите максимум 3 изображения.");
      event.target.value = "";
      return;
    }
    setImageError("");
    setImages(files);
  };

  const validateStep = () => {
    const nextErrors = {};
    if (step === 0) {
      if (!form.firstName.trim()) nextErrors.firstName = "Въведете име.";
      if (!form.lastName.trim()) nextErrors.lastName = "Въведете фамилия.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
        nextErrors.contactEmail = "Въведете валиден имейл адрес.";
      }
      if (form.contactPhone.trim().length < 6) nextErrors.contactPhone = "Въведете валиден телефон.";
      if (!form.source) nextErrors.source = "Изберете откъде научихте за нас.";
    }
    if (step === 1) {
      const hasPollutant = Object.keys(form.pollutants).some(
        (key) => key !== "otherText" && form.pollutants[key]
      );
      if (!hasPollutant) nextErrors.pollutants = "Изберете поне едно замърсяване.";
      if (!form.region.trim()) nextErrors.region = "Въведете район.";
      if (form.pollutants.other && !form.pollutants.otherText.trim()) {
        nextErrors.otherText = "Опишете другото замърсяване.";
      }
    }
    if (step === 2 && form.cleanedBefore === "yes" && !form.cleaningMethod.trim()) {
      nextErrors.cleaningMethod = "Посочете метода на почистване.";
    }
    if (step === 3 && !recaptchaToken) nextErrors.recaptcha = "Моля, потвърдете, че не сте робот.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep((current) => Math.min(current + 1, stepData.length - 1));
      setErrors({});
    }
  };

  const prevStep = () => {
    setStep((current) => Math.max(current - 1, 0));
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setStatusMessage(null);
    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      formData.append(key, typeof form[key] === "object" ? JSON.stringify(form[key]) : form[key]);
    });
    images.forEach((file) => { if (file) formData.append("images[]", file); });
    formData.append("recaptchaToken", recaptchaToken || "");

    try {
      const response = await fetch(submitUrl, { method: "POST", body: formData });
      const responseText = await response.text();
      if (!response.ok) throw new Error(responseText || "Грешка при изпращане.");
      setStatusMessage({ type: "success", text: "Запитването беше изпратено успешно!" });
      setSubmitted(true);
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message || "Неуспешно изпращане. Опитайте отново." });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm(createInitialForm());
    setImages([]);
    setImageError("");
    setRecaptchaToken(null);
    setStep(0);
    setErrors({});
    setStatusMessage(null);
  };

  const progress = ((step + 1) / stepData.length) * 100;

  return (
    <Box sx={{
      minHeight: "100%", py: { xs: 4, md: 7 }, px: { xs: 1.5, sm: 2 },
      background: "radial-gradient(circle at top left, rgba(66,165,245,0.10), transparent 32%), linear-gradient(180deg, #f5f8fc 0%, #ffffff 100%)"
    }}>
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
          <Box sx={{
            width: 64, height: 64, mx: "auto", mb: 2, display: "grid", placeItems: "center",
            borderRadius: 3, color: "#fff", background: "linear-gradient(135deg, #0b3c8c, #3476bd)",
            boxShadow: "0 10px 28px rgba(11,60,140,0.22)"
          }}>
            <SolarPowerRounded sx={{ fontSize: 34 }} />
          </Box>
          <Typography variant="overline" sx={{ color: "#0b3c8c", fontWeight: 800, letterSpacing: 2 }}>
            TC ENERGY · SOLAR CARE
          </Typography>
          <Typography component="h1" sx={{
            mt: 1, fontSize: { xs: "1.8rem", sm: "2.35rem", md: "2.8rem" },
            lineHeight: 1.15, fontWeight: 850, color: "#08264d"
          }}>
            Запитване за индивидуална оферта
          </Typography>
          <Typography sx={{ mt: 1.5, maxWidth: 650, mx: "auto", color: "text.secondary", lineHeight: 1.8 }}>
            Споделете информация за соларната централа и нуждите си, за да подготвим по-точно предложение.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{
          p: { xs: 2, sm: 3, md: 4 }, border: "1px solid #e3eaf3",
          borderRadius: { xs: 3, md: 4 }, boxShadow: "0 18px 55px rgba(20,50,90,0.08)", overflow: "hidden"
        }}>
          {!submitted && <>
            <Stepper activeStep={step} alternativeLabel sx={{
              mb: 3,
              "& .MuiStepLabel-label": { fontSize: { xs: "0.72rem", sm: "0.85rem" }, fontWeight: 700 },
              "& .MuiStepIcon-root.Mui-active": { color: "#0b3c8c" },
              "& .MuiStepIcon-root.Mui-completed": { color: "#2e7d32" }
            }}>
              {stepData.map((item) => <Step key={item.label}><StepLabel icon={item.icon}>{item.label}</StepLabel></Step>)}
            </Stepper>
            <LinearProgress variant="determinate" value={progress} sx={{
              mb: 4, height: 7, borderRadius: 99, backgroundColor: "#e9eff7",
              "& .MuiLinearProgress-bar": { borderRadius: 99, background: "linear-gradient(90deg, #0b3c8c, #42a5f5)" }
            }} />
          </>}

          {statusMessage && <Alert severity={statusMessage.type} sx={{ mb: 3, borderRadius: 2 }}>{statusMessage.text}</Alert>}

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div key="success" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <Box sx={{ textAlign: "center", py: { xs: 3, md: 6 } }}>
                  <TaskAltOutlined sx={{ fontSize: 64, color: "#2e7d32", mb: 2 }} />
                  <Typography component="h2" variant="h4" sx={{ fontWeight: 800, color: "#08264d", mb: 1.5 }}>
                    Благодарим ви!
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 4 }}>
                    Запитването е изпратено успешно. Ще се свържем с вас скоро.
                  </Typography>
                  <Button variant="contained" onClick={resetForm} sx={{
                    borderRadius: 2, px: 3, py: 1.3, fontWeight: 800, textTransform: "none",
                    background: "linear-gradient(90deg, #0b3c8c, #3476bd)"
                  }}>Попълнете нова анкета</Button>
                </Box>
              </motion.div>
            ) : (
              <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.22 }}>
                <CardContent sx={{ p: { xs: 0, sm: 1 } }}>
                  {step === 0 && <Box>
                    <Typography variant="h6" sx={{ color: "#08264d", fontWeight: 800, mb: 1 }}>Данни за контакт</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>Попълнете данните, по които можем да се свържем с вас.</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Име" fullWidth value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} error={!!errors.firstName} helperText={errors.firstName} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Фамилия" fullWidth value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} error={!!errors.lastName} helperText={errors.lastName} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Имейл" type="email" fullWidth value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} error={!!errors.contactEmail} helperText={errors.contactEmail} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Телефон" fullWidth value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} error={!!errors.contactPhone} helperText={errors.contactPhone} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.source} sx={fieldSx}>
                          <InputLabel id="source-label">Откъде научихте за нас?</InputLabel>
                          <Select labelId="source-label" label="Откъде научихте за нас?" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                            <MenuItem value="google">Google</MenuItem>
                            <MenuItem value="social">Социални мрежи</MenuItem>
                            <MenuItem value="friend">Препоръка</MenuItem>
                            <MenuItem value="other">Друго</MenuItem>
                          </Select>
                          {errors.source && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{errors.source}</Typography>}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>}

                  {step === 1 && <Box>
                    <Typography variant="h6" sx={{ color: "#08264d", fontWeight: 800, mb: 1 }}>Какви замърсявания има?</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>Изберете всички подходящи варианти.</Typography>
                    {errors.pollutants && <Alert severity="error" sx={{ mb: 2 }}>{errors.pollutants}</Alert>}
                    <Grid container spacing={1.5}>
                      {Object.entries(pollutantOptions).map(([key, label]) => <Grid item xs={12} sm={6} md={4} key={key}>
                        <Paper variant="outlined" onClick={() => togglePollutant(key)} sx={{
                          height: "100%", p: 1.2, borderRadius: 2, cursor: "pointer",
                          borderColor: form.pollutants[key] ? "#0b3c8c" : "#dce4ef",
                          backgroundColor: form.pollutants[key] ? "#edf4ff" : "#fff",
                          "&:hover": { borderColor: "#0b3c8c" }
                        }}>
                          <FormControlLabel sx={{ m: 0, width: "100%" }} control={
                            <Checkbox checked={form.pollutants[key]} onChange={() => togglePollutant(key)} />
                          } label={label} />
                        </Paper>
                      </Grid>)}
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, borderColor: form.pollutants.other ? "#0b3c8c" : "#dce4ef", backgroundColor: form.pollutants.other ? "#edf4ff" : "#fff" }}>
                          <FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={form.pollutants.other} onChange={() => togglePollutant("other")} />} label="Други" />
                        </Paper>
                      </Grid>
                      {form.pollutants.other && <Grid item xs={12}>
                        <TextField label="Опишете другото замърсяване" fullWidth value={form.pollutants.otherText} onChange={(e) => setForm({ ...form, pollutants: { ...form.pollutants, otherText: e.target.value } })} error={!!errors.otherText} helperText={errors.otherText} sx={fieldSx} />
                      </Grid>}
                      <Grid item xs={12}>
                        <TextField label="Район / населено място" fullWidth value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} error={!!errors.region} helperText={errors.region} sx={fieldSx} />
                      </Grid>
                    </Grid>
                  </Box>}

                  {step === 2 && <Box>
                    <Typography variant="h6" sx={{ color: "#08264d", fontWeight: 800, mb: 1 }}>Информация за почистването</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Ако сте дошли от калкулатора, данните са попълнени автоматично. Можете да ги редактирате.
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: "#d9e6f7", backgroundColor: "#f6f9ff" }}>
                      <Typography sx={{ fontWeight: 800, color: "#0b3c8c", mb: 1 }}>Почиствана ли е централата досега?</Typography>
                      <RadioGroup row value={form.cleanedBefore} onChange={(e) => setForm({ ...form, cleanedBefore: e.target.value })}>
                        <FormControlLabel value="yes" control={<Radio />} label="Да" />
                        <FormControlLabel value="no" control={<Radio />} label="Не" />
                      </RadioGroup>
                    </Paper>
                    {form.cleanedBefore === "yes" && <TextField label="С какъв метод е почиствана?" fullWidth value={form.cleaningMethod} onChange={(e) => setForm({ ...form, cleaningMethod: e.target.value })} error={!!errors.cleaningMethod} helperText={errors.cleaningMethod} sx={{ ...fieldSx, mb: 2 }} />}
                    <TextField label="Мощност на парка" fullWidth value={form.parkPower} onChange={(e) => setForm({ ...form, parkPower: e.target.value })} sx={{ ...fieldSx, mb: 2 }} />
                    <TextField label="Характеристики на панелите" fullWidth multiline minRows={2} value={form.panelSpecs} onChange={(e) => setForm({ ...form, panelSpecs: e.target.value })} sx={{ ...fieldSx, mb: 2 }} />
                    <TextField label="Брой панели / площ / изчисления от калкулатора" fullWidth multiline minRows={3} value={form.panelCountOrArea} onChange={(e) => setForm({ ...form, panelCountOrArea: e.target.value })} sx={fieldSx} />
                    <Divider sx={{ my: 3 }} />
                    <Typography sx={{ fontWeight: 800, color: "#08264d", mb: 1 }}>Снимки на панелите (по желание)</Typography>
                    <Button variant="outlined" component="label" startIcon={<CloudUploadOutlined />} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, px: 2.5, py: 1.2 }}>
                      Избери снимки
                      <input type="file" hidden multiple accept="image/*" onChange={handleImageChange} />
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      До 3 изображения. {images.length > 0 ? `Избрани: ${images.map((file) => file.name).join(", ")}` : "Няма избрани изображения."}
                    </Typography>
                    {imageError && <Alert severity="error" sx={{ mt: 1 }}>{imageError}</Alert>}
                  </Box>}

                  {step === 3 && <Box sx={{ textAlign: "center", py: 2 }}>
                    <Box sx={{ width: 58, height: 58, mx: "auto", mb: 2, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: "#edf4ff", color: "#0b3c8c" }}>
                      <TaskAltOutlined sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: "#08264d", fontWeight: 800, mb: 1 }}>Последна стъпка</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>Потвърдете, че не сте робот, след което изпратете запитването.</Typography>
                    {errors.recaptcha && <Alert severity="error" sx={{ mb: 2 }}>{errors.recaptcha}</Alert>}
                    <Box sx={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
                      <ReCAPTCHA sitekey={recaptchaKey} onChange={(token) => setRecaptchaToken(token)} onExpired={() => setRecaptchaToken(null)} />
                    </Box>
                  </Box>}

                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Button variant="outlined" disabled={step === 0 || submitting} onClick={prevStep} startIcon={<ArrowBackRounded />} sx={{ borderRadius: 2, px: 2.5, py: 1.2, textTransform: "none", fontWeight: 800 }}>
                      Назад
                    </Button>
                    <Typography variant="caption" color="text.secondary">Стъпка {step + 1} от {stepData.length}</Typography>
                    {step < stepData.length - 1 ? (
                      <Button variant="contained" onClick={nextStep} endIcon={<ArrowForwardRounded />} sx={{ borderRadius: 2, px: 3, py: 1.2, textTransform: "none", fontWeight: 800, background: "linear-gradient(90deg, #0b3c8c, #3476bd)" }}>
                        Напред
                      </Button>
                    ) : (
                      <Button variant="contained" onClick={handleSubmit} disabled={submitting} endIcon={<SendRounded />} sx={{ borderRadius: 2, px: 3, py: 1.2, textTransform: "none", fontWeight: 800, background: "linear-gradient(90deg, #0b3c8c, #3476bd)" }}>
                        {submitting ? "Изпращане..." : "Изпрати запитването"}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 2.5, lineHeight: 1.7 }}>
          Данните се използват за обработка на запитването ви и подготовка на индивидуална оферта.
        </Typography>
      </Box>
    </Box>
  );
}
